/**
 * Front-end security-profile defaults.
 *
 * The web package is decoupled from `@agent-world/core`, so these mirror the
 * shapes from `core/src/security/presets.ts`. A single "level" maps to a full,
 * valid `AgentSecurityProfile` — the UI only asks the user to pick a level.
 */

export type SecurityLevel = "trusted" | "semi-trusted" | "untrusted" | "read-only";

export interface AgentSecurityProfile {
  securityLevel: "trusted" | "semi-trusted" | "untrusted";
  mode: "read-write" | "read-only";
  toolPolicies: Array<{ pattern: string; action: string; reason?: string }>;
  contextAcl: {
    readPrefixes: string[];
    writePrefixes: string[];
    allowedEntryKinds: string[];
  };
  budget: { maxCostUsd: number; maxMessages: number };
  allowedCredentialKeys?: string[];
}

/** Build a complete, valid security profile from a UI-level choice. */
export function defaultSecurity(level: SecurityLevel = "semi-trusted"): AgentSecurityProfile {
  switch (level) {
    case "trusted":
      return {
        securityLevel: "trusted",
        mode: "read-write",
        toolPolicies: [],
        contextAcl: {
          readPrefixes: [""],
          writePrefixes: [""],
          allowedEntryKinds: ["artifact", "decision", "plan", "observation"],
        },
        budget: { maxCostUsd: 10.0, maxMessages: 100 },
      };
    case "untrusted":
      return {
        securityLevel: "untrusted",
        mode: "read-write",
        toolPolicies: [
          { pattern: "*", action: "ask", reason: "Untrusted agent — all tools require approval" },
        ],
        contextAcl: { readPrefixes: [], writePrefixes: [], allowedEntryKinds: [] },
        budget: { maxCostUsd: 1.0, maxMessages: 20 },
        allowedCredentialKeys: [],
      };
    case "read-only":
      return {
        securityLevel: "semi-trusted",
        mode: "read-only",
        toolPolicies: [],
        contextAcl: { readPrefixes: [""], writePrefixes: [], allowedEntryKinds: ["observation"] },
        budget: { maxCostUsd: 2.0, maxMessages: 30 },
      };
    case "semi-trusted":
    default:
      return {
        securityLevel: "semi-trusted",
        mode: "read-write",
        toolPolicies: [],
        contextAcl: {
          readPrefixes: [""],
          writePrefixes: [""],
          allowedEntryKinds: ["artifact", "observation"],
        },
        budget: { maxCostUsd: 5.0, maxMessages: 50 },
      };
  }
}

/** Maps a stored profile back to the closest UI level (for editing existing agents). */
export function securityLevelOf(profile: AgentSecurityProfile | undefined): SecurityLevel {
  if (!profile) return "semi-trusted";
  if (profile.mode === "read-only") return "read-only";
  if (profile.securityLevel === "trusted") return "trusted";
  if (profile.securityLevel === "untrusted") return "untrusted";
  return "semi-trusted";
}

export const SECURITY_LEVEL_OPTIONS: Array<{ value: SecurityLevel; label: string }> = [
  { value: "trusted", label: "Trusted" },
  { value: "semi-trusted", label: "Semi-trusted" },
  { value: "untrusted", label: "Untrusted" },
  { value: "read-only", label: "Read-only" },
];
